import { describeError, useApi } from '@classgo/app-kit';
import type { BillingCalendarView, CalendarRunResult } from '@classgo/shared';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Modal,
  Select,
  Table,
  useToast,
} from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { SchoolYearView } from '../../academic/types';
import { billingApi, billingQueries } from '../../billing/api';
import { useT } from '../../i18n';
import { formatDate, formatMonth, monthsBetween } from '../../lib/dates';
import { formatMoney } from '../../lib/money';
import { can } from '../../shell';
import { useTenant } from '../../tenant/tenant-provider';

/**
 * The billing calendar tab (ticket #117): every recurring concept of the year with the months it
 * charges, the due day and how many charges each month already has (what the day-1 job leaves
 * behind), the calendar's dialog (`PUT /billing/concepts/{id}/calendar`), and the run of one
 * month by hand with a preview first (`POST /billing/calendars/run`).
 *
 * @param {object} props -- The active school year.
 * @returns The tab.
 */
export const BillingCalendarTab = ({ year }: { year: SchoolYearView }) => {
  const t = useT();
  const api = useApi();
  const { session } = useTenant();
  const calendars = useQuery(billingQueries.calendars(api, year.id));
  const [editing, setEditing] = useState<BillingCalendarView | null>(null);
  const queries = useQueryClient();
  const canConfigure = can(session, 'billing:configure');
  const yearMonths = monthsBetween(year.startsOn, year.endsOn);
  const items = calendars.data?.items ?? [];
  return (
    <div className="space-y-4">
      <Card title={t('billing.calendar.title', { year: year.name })} flush>
        <p className="px-4 pt-4 text-sm text-gray-600">{t('billing.calendar.intro')}</p>
        {calendars.isError ? (
          <div className="p-6">
            <EmptyState
              title={t('entity.error.title')}
              description={describeError(t, calendars.error)}
            />
          </div>
        ) : (
          <Table<BillingCalendarView>
            caption={t('billing.calendar.caption')}
            rows={items}
            rowKey={(row) => row.feeConceptId}
            loading={calendars.isPending}
            {...(canConfigure ? { onRowClick: (row: BillingCalendarView) => setEditing(row) } : {})}
            empty={
              <EmptyState
                title={t('billing.calendar.empty.title')}
                description={t('billing.calendar.empty.body')}
              />
            }
            columns={[
              {
                key: 'concept',
                header: t('billing.calendar.column.concept'),
                cell: (row) => (
                  <div>
                    <p className="font-medium text-gray-900">{row.conceptName}</p>
                    <p className="text-xs text-gray-500">{t(`billing.code.${row.conceptCode}`)}</p>
                  </div>
                ),
              },
              {
                key: 'dueDay',
                header: t('billing.calendar.column.dueDay'),
                cell: (row) =>
                  !row.configured ? (
                    <Badge tone="warning">{t('billing.calendar.notConfigured')}</Badge>
                  ) : !row.enabled ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <span>{row.dueDay}</span>
                      <Badge tone="gray">{t('billing.calendar.disabled')}</Badge>
                    </span>
                  ) : (
                    row.dueDay
                  ),
              },
              {
                key: 'months',
                header: t('billing.calendar.column.months'),
                cell: (row) =>
                  row.months.length === 0 ? (
                    t('common.none')
                  ) : (
                    <ul className="flex flex-wrap gap-2">
                      {row.months.map((month) => (
                        <li key={month.periodLabel}>
                          <Badge tone={month.charges > 0 ? 'success' : 'gray'}>
                            {month.charges === 0
                              ? t('billing.calendar.month.pending', {
                                  month: formatMonth(month.periodLabel),
                                })
                              : month.charges === 1
                                ? t('billing.calendar.month.generatedOne', {
                                    month: formatMonth(month.periodLabel),
                                  })
                                : t('billing.calendar.month.generated', {
                                    month: formatMonth(month.periodLabel),
                                    count: month.charges,
                                  })}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  ),
              },
            ]}
          />
        )}
      </Card>
      {can(session, 'billing:write') ? (
        <RunMonth calendars={items} yearMonths={yearMonths} />
      ) : null}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={t('billing.calendar.edit', { name: editing?.conceptName ?? '' })}
        size="lg"
      >
        {editing ? (
          <CalendarForm
            calendar={editing}
            yearMonths={yearMonths}
            onDone={() => {
              setEditing(null);
              void queries.invalidateQueries({ queryKey: ['billing', 'calendars'] });
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

const CalendarForm = ({
  calendar,
  yearMonths,
  onDone,
}: {
  calendar: BillingCalendarView;
  yearMonths: string[];
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const [enabled, setEnabled] = useState(calendar.configured ? calendar.enabled : true);
  const [dueDay, setDueDay] = useState(String(calendar.dueDay ?? 5));
  const [months, setMonths] = useState<string[]>(calendar.months.map((month) => month.periodLabel));
  const [error, setError] = useState<string | null>(null);
  // The months on offer: the school year's, plus any saved month outside it so it can be unmarked.
  const offered = [...new Set([...yearMonths, ...months])].sort();
  const save = useMutation({
    mutationFn: () =>
      billingApi(api).putCalendar(calendar.feeConceptId, {
        enabled,
        dueDay: Number(dueDay),
        months: offered.filter((month) => months.includes(month)),
      }),
    onSuccess: () => {
      toast.show({ tone: 'success', title: t('billing.calendar.saved') });
      onDone();
    },
    onError: (thrown) => setError(describeError(t, thrown)),
  });
  const toggle = (month: string, checked: boolean) =>
    setMonths((current) =>
      checked ? [...current, month] : current.filter((value) => value !== month),
    );
  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (months.length === 0) {
          setError(t('billing.calendar.noMonths'));
          return;
        }
        setError(null);
        save.mutate();
      }}
    >
      {error ? (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      ) : null}
      <Checkbox
        label={t('billing.calendar.field.enabled')}
        description={t('billing.calendar.field.enabled.help')}
        checked={enabled}
        onChange={(event) => setEnabled(event.target.checked)}
      />
      <Input
        label={t('billing.calendar.field.dueDay')}
        help={t('billing.calendar.field.dueDay.help')}
        type="number"
        min={1}
        max={28}
        step={1}
        required
        value={dueDay}
        onChange={(event) => setDueDay(event.target.value)}
      />
      <fieldset>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <legend className="text-sm font-medium text-gray-900">
              {t('billing.calendar.field.months')}
            </legend>
            <p className="text-xs text-gray-500">{t('billing.calendar.field.months.help')}</p>
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={() => setMonths(offered)}>
            {t('billing.calendar.allMonths')}
          </Button>
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          {offered.map((month) => (
            <Checkbox
              key={month}
              label={formatMonth(month)}
              checked={months.includes(month)}
              onChange={(event) => toggle(month, event.target.checked)}
            />
          ))}
        </div>
      </fieldset>
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
};

const RunMonth = ({
  calendars,
  yearMonths,
}: {
  calendars: BillingCalendarView[];
  yearMonths: string[];
}) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const current = new Date().toISOString().slice(0, 7);
  const [periodLabel, setPeriodLabel] = useState(
    yearMonths.includes(current) ? current : (yearMonths[0] ?? current),
  );
  const [conceptId, setConceptId] = useState('');
  const [preview, setPreview] = useState<CalendarRunResult | null>(null);
  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      billingApi(api).runCalendars({
        periodLabel,
        ...(conceptId ? { feeConceptId: conceptId } : {}),
        dryRun,
      }),
    onSuccess: (result, dryRun) => {
      if (dryRun) {
        setPreview(result);
        return;
      }
      setPreview(null);
      void queries.invalidateQueries({ queryKey: ['billing'] });
      toast.show({
        tone: 'success',
        title: t('billing.generate.done'),
        description: t('billing.generate.summary', {
          created: sum(result, 'created'),
          skipped: sum(result, 'skipped'),
          total: formatMoney(sum(result, 'totalAmount')),
        }),
      });
    },
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('billing.run.title'),
        description: describeError(t, error),
      }),
  });
  const creatable = preview ? sum(preview, 'created') : 0;
  return (
    <Card title={t('billing.run.title')}>
      <p className="mb-3 text-sm text-gray-600">{t('billing.run.intro')}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label={t('billing.run.month')}
          value={periodLabel}
          onChange={(event) => {
            setPeriodLabel(event.target.value);
            setPreview(null);
          }}
          options={yearMonths.map((month) => ({ value: month, label: formatMonth(month) }))}
        />
        <Select
          label={t('billing.run.concept')}
          value={conceptId}
          onChange={(event) => {
            setConceptId(event.target.value);
            setPreview(null);
          }}
          options={[
            { value: '', label: t('billing.run.allConcepts') },
            ...calendars
              .filter((row) => row.configured)
              .map((row) => ({ value: row.feeConceptId, label: row.conceptName })),
          ]}
        />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="secondary" disabled={run.isPending} onClick={() => run.mutate(true)}>
          {t('billing.generate.preview')}
        </Button>
        <Button disabled={creatable === 0 || run.isPending} onClick={() => run.mutate(false)}>
          {t('billing.generate.confirm', { count: creatable })}
        </Button>
      </div>
      {preview ? (
        preview.items.length === 0 ? (
          <p role="status" className="mt-4 text-sm text-warning-700">
            {t('billing.run.nothing')}
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            <Table<CalendarRunResult['items'][number]>
              caption={t('billing.run.caption')}
              rows={preview.items}
              rowKey={(row) => row.feeConceptId}
              columns={[
                {
                  key: 'concept',
                  header: t('billing.run.column.concept'),
                  cell: (row) => row.conceptName,
                },
                {
                  key: 'dueOn',
                  header: t('billing.statement.column.dueOn'),
                  cell: (row) => formatDate(row.dueOn),
                },
                {
                  key: 'created',
                  header: t('billing.generate.totals.created'),
                  align: 'right',
                  cell: (row) => row.created,
                },
                {
                  key: 'skipped',
                  header: t('billing.generate.totals.skipped'),
                  align: 'right',
                  cell: (row) => row.skipped,
                },
                {
                  key: 'withoutFamily',
                  header: t('billing.generate.totals.withoutFamily'),
                  align: 'right',
                  cell: (row) => row.withoutFamily.length,
                },
                {
                  key: 'total',
                  header: t('billing.generate.totals.total'),
                  align: 'right',
                  cell: (row) => formatMoney(row.totalAmount),
                },
              ]}
            />
            {preview.items.some((row) => row.withoutFamily.length > 0) ? (
              <p role="status" className="text-sm text-warning-700">
                {t('billing.generate.withoutFamily', {
                  names: [
                    ...new Set(
                      preview.items.flatMap((row) =>
                        row.withoutFamily.map(
                          (student) => student.studentName ?? student.studentUserId,
                        ),
                      ),
                    ),
                  ].join(', '),
                })}
              </p>
            ) : null}
          </div>
        )
      ) : null}
    </Card>
  );
};

const sum = (result: CalendarRunResult, key: 'created' | 'skipped' | 'totalAmount') =>
  result.items.reduce((total, row) => total + row[key], 0);
